import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Eye, Printer, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { formatCurrency, formatDateTimeDR, formatDateDR, getLocalDateString } from '../utils/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Returns = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtros de fecha - zona horaria RD: UTC-4
  // Para inputs type="date" necesitamos formato YYYY-MM-DD
  const hoyInput = getLocalDateString(); // Retorna YYYY-MM-DD
  
  // Convertir formato YYYY-MM-DD a DD/MM/YYYY para validación y backend
  const formatInputToDisplay = (inputDate) => {
    if (!inputDate) return '';
    const [year, month, day] = inputDate.split('-');
    return `${day}/${month}/${year}`;
  };
  
  // Convertir formato DD/MM/YYYY a YYYY-MM-DD para input
  const formatDisplayToInput = (displayDate) => {
    if (!displayDate) return '';
    const [day, month, year] = displayDate.split('/');
    return `${year}-${month}-${day}`;
  };
  
  // Inicializar con formato DD/MM/YYYY para validación correcta
  const hoyDisplay = formatInputToDisplay(hoyInput);
  
  const [fechaDesde, setFechaDesde] = useState(hoyDisplay);
  const [fechaHasta, setFechaHasta] = useState(hoyDisplay);
  
  // Filtros para modal de facturas
  const [invoiceFechaDesde, setInvoiceFechaDesde] = useState(hoyDisplay);
  const [invoiceFechaHasta, setInvoiceFechaHasta] = useState(hoyDisplay);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invoiceSearchDialogOpen, setInvoiceSearchDialogOpen] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [tipoNota, setTipoNota] = useState('credito');
  const [motivo, setMotivo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [returnItems, setReturnItems] = useState([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [invoiceAvailability, setInvoiceAvailability] = useState(null);

  const motivosCredito = [
    { value: 'Devolución de mercancía', label: 'Devolución de mercancía' },
    { value: 'Error en facturación', label: 'Error en facturación' },
  ];

  const motivosDebito = [
    { value: 'Devolución de mercancía', label: 'Devolución de mercancía' },
    { value: 'Error en facturación', label: 'Error en facturación' },
  ];

  useEffect(() => {
    if (fechaDesde && fechaHasta && isValidDate(fechaDesde) && isValidDate(fechaHasta)) {
      fetchNotes(fechaDesde, fechaHasta);
    }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, []);

  const isValidDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return false;
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(regex);
    if (!match) return false;
    const [, day, month, year] = match;
    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);
    if (m < 1 || m > 12) return false;
    if (d < 1 || d > 31) return false;
    if (y < 2000 || y > 2100) return false;
    return true;
  };

  const handleDatePickerChange = (e, setter) => {
    const value = e.target.value;
    if (value) {
      const [year, month, day] = value.split('-');
      setter(`${day}/${month}/${year}`);
    }
  };

  const toDatePickerFormat = (dateStr) => {
    if (!dateStr || !isValidDate(dateStr)) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  const fetchNotes = async (desde = fechaDesde, hasta = fechaHasta) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (desde && isValidDate(desde)) params.append('fecha_desde', desde);
      if (hasta && isValidDate(hasta)) params.append('fecha_hasta', hasta);
      
      const response = await axios.get(`${API}/notes?${params.toString()}`);
      setNotes(response.data);
    } catch (error) {
      toast.error('Error al cargar devoluciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      // Filtrar facturas que pueden tener devoluciones (activa, pagada, devuelta_parcial)
      setInvoices(response.data.filter(inv => 
        inv.status === 'activa' || inv.status === 'pagada' || inv.status === 'devuelta_parcial'
      ));
    } catch (error) {
      toast.error('Error al cargar facturas');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const getClientCode = (clientName) => {
    const client = clients.find(c => 
      c.nombre?.toLowerCase() === clientName?.toLowerCase()
    );
    return client?.codigo_cliente || '-';
  };

  const compareDates = (date1, date2) => {
    const [d1, m1, y1] = date1.split('/');
    const [d2, m2, y2] = date2.split('/');
    const dt1 = new Date(y1, m1 - 1, d1);
    const dt2 = new Date(y2, m2 - 1, d2);
    return dt1 - dt2;
  };

  // Seleccionar factura y cargar disponibilidad
  const selectInvoice = async (invoice) => {
    try {
      // Obtener disponibilidad de devolución
      const response = await axios.get(`${API}/invoices/${invoice.id}/devolucion-disponible`);
      const availability = response.data;
      
      if (!availability.puede_devolver) {
        toast.error('Esta factura ya fue devuelta en su totalidad');
        return;
      }
      
      setInvoiceAvailability(availability);
      setSelectedInvoice(invoice);
      
      // Inicializar items con cantidades disponibles
      setReturnItems(availability.items.map(item => ({
        producto_id: item.producto_id,
        producto_nombre: item.producto_nombre,
        cantidad_facturada: item.cantidad_facturada,
        cantidad_devuelta: item.cantidad_devuelta,
        cantidad_disponible: item.cantidad_disponible,
        cantidad_devolver: 0,
        precio_unitario: item.precio_unitario,
        puede_devolver: item.puede_devolver
      })));
      
      setInvoiceSearchDialogOpen(false);
      setDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar disponibilidad');
    }
  };

  const handleQuantityChange = (index, value) => {
    const newItems = [...returnItems];
    const cantidad = parseInt(value) || 0;
    
    if (cantidad > newItems[index].cantidad_disponible) {
      toast.error(`Solo puede devolver hasta ${newItems[index].cantidad_disponible} unidades de este producto`);
      return;
    }
    
    newItems[index].cantidad_devolver = cantidad;
    setReturnItems(newItems);
  };

  // Botón "Devolver todo" - llena todas las cantidades disponibles
  const handleDevolverTodo = () => {
    const newItems = returnItems.map(item => ({
      ...item,
      cantidad_devolver: item.cantidad_disponible
    }));
    setReturnItems(newItems);
    toast.success('Se han seleccionado todos los productos disponibles para devolver');
  };

  const handleSubmit = async () => {
    try {
      // Filtrar solo items con cantidad > 0
      const itemsToReturn = returnItems
        .filter(item => item.cantidad_devolver > 0)
        .map(item => ({
          producto_id: item.producto_id,
          producto_nombre: item.producto_nombre,
          cantidad: item.cantidad_devolver,
          precio_unitario: item.precio_unitario,
          subtotal: item.precio_unitario * item.cantidad_devolver,
          descuento: 0
        }));

      if (itemsToReturn.length === 0) {
        toast.error('Debe seleccionar al menos un artículo para devolver');
        return;
      }

      if (!motivo) {
        toast.error('Debe seleccionar un motivo');
        return;
      }

      const noteData = {
        tipo_nota: tipoNota,
        factura_id: selectedInvoice.id,
        motivo: motivo,
        descripcion: descripcion,
        items: itemsToReturn,
        devolver_inventario: true
      };

      await axios.post(`${API}/notes`, noteData);
      toast.success(`Nota de ${tipoNota === 'credito' ? 'Crédito' : 'Débito'} creada exitosamente`);
      
      fetchNotes(fechaDesde, fechaHasta);
      fetchInvoices(); // Recargar facturas para actualizar estados
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la nota');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setSelectedInvoice(null);
    setInvoiceAvailability(null);
    setTipoNota('credito');
    setMotivo('');
    setDescripcion('');
    setReturnItems([]);
  };

  const viewNoteDetail = (note) => {
    setSelectedNote(note);
    setDetailDialogOpen(true);
  };

  const printNote = (note) => {
    const printWindow = window.open('', '', 'width=302,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${note.tipo_nota === 'credito' ? 'Nota de Crédito' : 'Nota de Débito'} ${note.numero_nota}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: 80mm auto; margin: 0; }
            body { font-family: 'Courier New', monospace; font-size: 11px; line-height: 1.4; width: 80mm; padding: 3mm; }
            .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 3mm; margin-bottom: 3mm; }
            h2 { font-size: 14px; margin: 2mm 0; }
            .info { margin-bottom: 3mm; font-size: 10px; }
            .info-row { display: flex; justify-content: space-between; margin: 1mm 0; }
            .separator { border-top: 1px dashed #000; margin: 3mm 0; }
            .totals { margin-top: 3mm; border-top: 1px dashed #000; padding-top: 3mm; }
            .total-row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 11px; }
            .grand-total { font-weight: bold; font-size: 13px; border-top: 2px solid #000; padding-top: 3mm; margin-top: 2mm; }
            .footer { text-align: center; margin-top: 5mm; font-size: 9px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${note.tipo_nota === 'credito' ? 'NOTA DE CRÉDITO' : 'NOTA DE DÉBITO'}</h2>
            <p><strong>${note.numero_nota}</strong></p>
            ${note.ncf ? `<p>NCF: ${note.ncf}</p>` : ''}
          </div>
          
          <div class="info">
            <div class="info-row"><span>Fecha:</span><span>${note.fecha}</span></div>
            <div class="info-row"><span>Factura Ref:</span><span>${note.factura_numero}</span></div>
            <div class="info-row"><span>Motivo:</span><span>${note.motivo}</span></div>
          </div>

          <div class="separator"></div>

          <div class="info">
            <div><strong>CLIENTE:</strong></div>
            <div>${note.cliente.nombre}</div>
            ${note.cliente.rnc ? `<div>RNC: ${note.cliente.rnc}</div>` : ''}
          </div>

          <div class="separator"></div>

          <div class="items">
            <div style="font-weight: bold; margin-bottom: 2mm;">ARTÍCULOS:</div>
            ${note.items.map(item => `
              <div style="margin: 2mm 0;">
                <div>${item.producto_nombre}</div>
                <div style="display: flex; justify-content: space-between; font-size: 10px;">
                  <span>${item.cantidad} x RD$ ${item.precio_unitario.toFixed(2)}</span>
                  <span>RD$ ${item.subtotal.toFixed(2)}</span>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="totals">
            <div class="total-row"><span>Subtotal:</span><span>RD$ ${note.subtotal.toFixed(2)}</span></div>
            ${note.itbis > 0 ? `<div class="total-row"><span>ITBIS:</span><span>RD$ ${note.itbis.toFixed(2)}</span></div>` : ''}
            <div class="total-row grand-total"><span>TOTAL:</span><span>RD$ ${note.total.toFixed(2)}</span></div>
          </div>

          <div class="footer">
            <p>Documento válido como comprobante fiscal</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); setTimeout(function() { window.close(); }, 100); }, 250);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Obtener badge de estado de factura
  const getStatusBadge = (status) => {
    const statusConfig = {
      'activa': { label: 'Activa', class: 'bg-emerald-100 text-emerald-700' },
      'pagada': { label: 'Pagada', class: 'bg-blue-100 text-blue-700' },
      'devuelta_parcial': { label: 'Dev. Parcial', class: 'bg-amber-100 text-amber-700' },
      'devuelta_total': { label: 'Dev. Total', class: 'bg-red-100 text-red-700' },
      'anulada': { label: 'Anulada', class: 'bg-slate-100 text-slate-600' },
    };
    const config = statusConfig[status] || { label: status, class: 'bg-slate-100 text-slate-600' };
    return <span className={`px-2 py-1 text-xs font-medium rounded ${config.class}`}>{config.label}</span>;
  };

  const filteredNotes = notes.filter(note =>
    note.numero_nota.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.factura_numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInvoices = invoices.filter(inv => {
    const codigoCliente = getClientCode(inv.cliente?.nombre);
    const matchesSearch = 
      inv.numero_factura.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
      inv.cliente.nombre.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
      codigoCliente.toLowerCase().includes(invoiceSearchTerm.toLowerCase());
    
    if ((invoiceFechaDesde && isValidDate(invoiceFechaDesde)) || 
        (invoiceFechaHasta && isValidDate(invoiceFechaHasta))) {
      const fechaFactura = inv.fecha.split(' ')[0];
      
      if (invoiceFechaDesde && isValidDate(invoiceFechaDesde)) {
        if (compareDates(fechaFactura, invoiceFechaDesde) < 0) return false;
      }
      
      if (invoiceFechaHasta && isValidDate(invoiceFechaHasta)) {
        if (compareDates(fechaFactura, invoiceFechaHasta) > 0) return false;
      }
    }
    
    return matchesSearch;
  });

  const canCreate = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'cajero' || user?.role === 'gerente';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Devoluciones</h1>
          <p className="text-slate-600 mt-1">Gestión de Notas de Crédito y Débito</p>
        </div>

        {canCreate && (
          <Button
            onClick={() => {
              setInvoiceSearchTerm('');
              setInvoiceFechaDesde(hoyInput);
              setInvoiceFechaHasta(hoyInput);
              setInvoiceSearchDialogOpen(true);
            }}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <Plus className="mr-2" size={18} />
            Nueva Devolución
          </Button>
        )}
      </div>

      {/* Filtros de Rango de Fecha */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fecha-desde" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Desde
            </Label>
            <Input
              id="fecha-desde"
              type="date"
              value={toDatePickerFormat(fechaDesde)}
              onChange={(e) => handleDatePickerChange(e, setFechaDesde)}
              className="w-full"
            />
          </div>
          <div>
            <Label htmlFor="fecha-hasta" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Hasta
            </Label>
            <Input
              id="fecha-hasta"
              type="date"
              value={toDatePickerFormat(fechaHasta)}
              onChange={(e) => handleDatePickerChange(e, setFechaHasta)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar devoluciones por número, factura, cliente..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Notes Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
            <thead>
              <tr>
                <th>No. Devolución</th>
                <th>Tipo</th>
                <th>Factura Ref.</th>
                <th>Cliente</th>
                <th>Fecha</th>
                <th>Motivo</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotes.map((note) => (
                <tr key={note.id}>
                  <td className="font-medium">{note.numero_nota}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      note.tipo_nota === 'credito' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {note.tipo_nota === 'credito' ? 'Crédito' : 'Débito'}
                    </span>
                  </td>
                  <td className="text-sm text-slate-600">{note.factura_numero}</td>
                  <td>
                    <div className="font-medium">{note.cliente.nombre}</div>
                    {note.cliente.rnc && <div className="text-xs text-slate-500">RNC: {note.cliente.rnc}</div>}
                  </td>
                  <td className="text-sm">{note.fecha}</td>
                  <td className="text-sm text-slate-600">{note.motivo}</td>
                  <td className="font-semibold text-green-600">{formatCurrency(note.total)}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      note.status === 'activa' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {note.status === 'activa' ? 'Activa' : 'Anulada'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => viewNoteDetail(note)} title="Ver detalle">
                        <Eye size={16} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => printNote(note)} title="Imprimir">
                        <Printer size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredNotes.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron devoluciones en el rango de fechas seleccionado
          </div>
        )}
      </div>

      {/* Modal: Buscar Factura */}
      <Dialog open={invoiceSearchDialogOpen} onOpenChange={setInvoiceSearchDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Seleccionar Factura para Devolución</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1 block">Fecha Desde</Label>
                  <Input
                    type="date"
                    value={toDatePickerFormat(invoiceFechaDesde)}
                    onChange={(e) => handleDatePickerChange(e, setInvoiceFechaDesde)}
                    className="w-full"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1 block">Fecha Hasta</Label>
                  <Input
                    type="date"
                    value={toDatePickerFormat(invoiceFechaHasta)}
                    onChange={(e) => handleDatePickerChange(e, setInvoiceFechaHasta)}
                    className="w-full"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={() => { setInvoiceFechaDesde(''); setInvoiceFechaHasta(''); }}
                    className="w-full"
                  >
                    Limpiar Filtros
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por número de factura, código cliente (CLI-000...) o nombre..."
                value={invoiceSearchTerm}
                onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-3 font-semibold">Factura</th>
                    <th className="text-left p-3 font-semibold">Código</th>
                    <th className="text-left p-3 font-semibold">Cliente</th>
                    <th className="text-left p-3 font-semibold">Fecha</th>
                    <th className="text-center p-3 font-semibold">Estado</th>
                    <th className="text-right p-3 font-semibold">Total</th>
                    <th className="text-center p-3 font-semibold">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map(invoice => (
                    <tr key={invoice.id} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-mono font-medium">{invoice.numero_factura}</td>
                      <td className="p-3 font-mono text-blue-600 font-medium">{getClientCode(invoice.cliente?.nombre)}</td>
                      <td className="p-3">{invoice.cliente.nombre}</td>
                      <td className="p-3 text-slate-600">{invoice.fecha}</td>
                      <td className="p-3 text-center">{getStatusBadge(invoice.status)}</td>
                      <td className="p-3 text-right font-semibold text-green-600">{formatCurrency(invoice.total)}</td>
                      <td className="p-3 text-center">
                        <Button 
                          size="sm" 
                          onClick={() => selectInvoice(invoice)}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          disabled={invoice.status === 'devuelta_total'}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredInvoices.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron facturas con los filtros aplicados
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear Nota */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Nota de {tipoNota === 'credito' ? 'Crédito' : 'Débito'}</DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-4">
              {/* Info de Factura */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Factura</Label>
                    <p className="font-semibold">{selectedInvoice.numero_factura}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Cliente</Label>
                    <p className="font-semibold">{selectedInvoice.cliente.nombre}</p>
                    <p className="text-xs text-blue-600 font-mono">{getClientCode(selectedInvoice.cliente?.nombre)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Total Factura</Label>
                    <p className="font-semibold text-green-600">{formatCurrency(selectedInvoice.total)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Estado</Label>
                    <div className="mt-1">{getStatusBadge(selectedInvoice.status)}</div>
                  </div>
                </div>
                {invoiceAvailability && invoiceAvailability.total_devoluciones > 0 && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                    ⚠️ Esta factura tiene {invoiceAvailability.total_devoluciones} devolución(es) previa(s). 
                    Solo puede devolver las cantidades disponibles.
                  </div>
                )}
              </div>

              {/* Tipo de Nota y Motivo */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Nota</Label>
                  <Select value={tipoNota} onValueChange={(value) => { setTipoNota(value); setMotivo(''); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credito">Nota de Crédito</SelectItem>
                      <SelectItem value="debito">Nota de Débito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Motivo *</Label>
                  <Select value={motivo} onValueChange={setMotivo}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione un motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {(tipoNota === 'credito' ? motivosCredito : motivosDebito).map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Descripción */}
              <div className="space-y-2">
                <Label>Descripción Adicional</Label>
                <Input
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Detalles adicionales..."
                />
              </div>

              {/* Items a Devolver */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Artículos a Devolver</Label>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleDevolverTodo}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    <RotateCcw size={16} className="mr-2" />
                    Devolver Todo
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3">Producto</th>
                        <th className="text-center p-3">Facturada</th>
                        <th className="text-center p-3">Ya Devuelta</th>
                        <th className="text-center p-3">Disponible</th>
                        <th className="text-center p-3">A Devolver</th>
                        <th className="text-right p-3">Precio</th>
                        <th className="text-right p-3">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnItems.map((item, index) => (
                        <tr key={index} className={`border-b ${!item.puede_devolver ? 'bg-slate-50 opacity-60' : ''}`}>
                          <td className="p-3">
                            {item.producto_nombre}
                            {!item.puede_devolver && (
                              <span className="block text-xs text-red-500">Ya devuelto totalmente</span>
                            )}
                          </td>
                          <td className="p-3 text-center">{item.cantidad_facturada}</td>
                          <td className="p-3 text-center text-amber-600 font-medium">{item.cantidad_devuelta}</td>
                          <td className="p-3 text-center text-emerald-600 font-medium">{item.cantidad_disponible}</td>
                          <td className="p-3">
                            <Input
                              type="number"
                              min="0"
                              max={item.cantidad_disponible}
                              value={item.cantidad_devolver}
                              onChange={(e) => handleQuantityChange(index, e.target.value)}
                              className="w-20 text-center mx-auto"
                              disabled={!item.puede_devolver}
                            />
                          </td>
                          <td className="p-3 text-right">{formatCurrency(item.precio_unitario)}</td>
                          <td className="p-3 text-right font-medium">
                            {formatCurrency(item.precio_unitario * item.cantidad_devolver)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Total */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total de la Nota:</span>
                  <span className="text-green-600">
                    {formatCurrency(
                      returnItems.reduce((sum, item) => 
                        sum + (item.precio_unitario * item.cantidad_devolver), 0
                      )
                    )}
                  </span>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700">
                  Crear Nota
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Detalle de Nota */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Detalle de {selectedNote?.tipo_nota === 'credito' ? 'Nota de Crédito' : 'Nota de Débito'}
            </DialogTitle>
          </DialogHeader>

          {selectedNote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500">Número</Label>
                  <p className="font-semibold">{selectedNote.numero_nota}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500">NCF</Label>
                  <p className="font-semibold">{selectedNote.ncf || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500">Factura de Referencia</Label>
                  <p className="font-semibold">{selectedNote.factura_numero}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <Label className="text-xs text-slate-500">Fecha</Label>
                  <p className="font-semibold">{selectedNote.fecha}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg col-span-2">
                  <Label className="text-xs text-slate-500">Cliente</Label>
                  <p className="font-semibold">{selectedNote.cliente.nombre}</p>
                  {selectedNote.cliente.rnc && <p className="text-sm text-slate-500">RNC: {selectedNote.cliente.rnc}</p>}
                </div>
                <div className="bg-slate-50 p-4 rounded-lg col-span-2">
                  <Label className="text-xs text-slate-500">Motivo</Label>
                  <p className="font-semibold">{selectedNote.motivo}</p>
                  {selectedNote.descripcion && <p className="text-sm text-slate-500">{selectedNote.descripcion}</p>}
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3">Producto</th>
                      <th className="text-center p-3">Cantidad</th>
                      <th className="text-right p-3">Precio</th>
                      <th className="text-right p-3">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedNote.items.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-3">{item.producto_nombre}</td>
                        <td className="p-3 text-center">{item.cantidad}</td>
                        <td className="p-3 text-right">{formatCurrency(item.precio_unitario)}</td>
                        <td className="p-3 text-right font-medium">{formatCurrency(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr>
                      <td colSpan="3" className="p-3 text-right font-semibold">Subtotal:</td>
                      <td className="p-3 text-right font-semibold">{formatCurrency(selectedNote.subtotal)}</td>
                    </tr>
                    {selectedNote.itbis > 0 && (
                      <tr>
                        <td colSpan="3" className="p-3 text-right font-semibold">ITBIS:</td>
                        <td className="p-3 text-right font-semibold">{formatCurrency(selectedNote.itbis)}</td>
                      </tr>
                    )}
                    <tr className="bg-emerald-50">
                      <td colSpan="3" className="p-3 text-right font-bold text-lg">Total:</td>
                      <td className="p-3 text-right font-bold text-lg text-emerald-600">{formatCurrency(selectedNote.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Cerrar
                </Button>
                <Button onClick={() => printNote(selectedNote)} className="bg-blue-500 hover:bg-blue-600">
                  <Printer size={16} className="mr-2" />
                  Imprimir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Returns;
